import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef } from 'react';
import { node3dGroupSpread, transformFromNode3DProperties, } from '../../../r3f/nodeTransform';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { useResourceResolution } from '../../../resources/useSubOrExtResource';
import { drawnTag, useMeshContent, } from '../../../r3f/meshes/meshContent';
import { geometryDrawsPoints } from '../../../resources/meshes/godotSurface';
import { resolveMaterialSource } from '../../../r3f/materials/materialSource';
import { SurfaceMaterialSlots } from '../../../r3f/materials/SurfaceMaterialSlots';
import { SurfaceMaterialSlot } from '../../../r3f/materials/SurfaceMaterialSlot';
import { overlayDrawProps } from '../../../r3f/materials/materialOverlay';
import { NOTHING_DRAWN, NextPassChain, NextPassLink, drawnElementOf, useDrawnGeometry, } from '../../../r3f/materials/nextPass';
import { isOutgrownBySkin, useSkinSkeleton } from '../../../r3f/meshes/skinSkeleton';
import { useNodePath } from '../../../r3f/contexts/NodePathContext';
import { useMeshSkin } from './useMeshSkin';
import { wireGizmoProgram } from '../../../r3f/components/wireGizmoProgram';
import { visualLayersUserData } from '../../../r3f/visualLayers';
import { drawHookProps } from '../../../r3f/shadowCasting';
import { FramePoint } from '../../../r3f/components/NodeAabb';
import { useGeometryInstance } from '../../../r3f/visibilityRange/geometryInstance';
import { partsPlacement, UNPLACED } from '../../../r3f/visibilityRange/placements';
/** Literal-only, so its key is constant and it never remounts. */
const UNRESOLVED_MESH_MATERIAL = wireGizmoProgram(0xff00ff);
export function MeshInstance3D({ node, children, gizmo }) {
    const properties = node.properties;
    const pools = useSceneResources();
    const transform = useMemo(() => transformFromNode3DProperties(properties), [properties]);
    // A `.glb` resolves to nothing and falls through to the placeholder.
    const content = useMeshContent(useResourceResolution(properties.mesh, pools));
    // `material_override` and `surface_material_override/N`, keyed by the mesh's own surface
    // numbering. Godot applies them to every kind of mesh alike.
    const meshOverrides = useMemo(() => resolveMeshOverrides(properties, pools), [properties, pools]);
    // `material_overlay` never competes for a surface slot, so it resolves apart from the
    // overrides: it is drawn on its own.
    const overlaySource = useMemo(() => resolveMaterialSource(properties.materialOverlay, pools), [properties.materialOverlay, pools]);
    // The ref lands on whichever `<mesh>` or `<points>` MeshShell renders, for the overlay to share.
    const meshRef = useRef(null);
    // GeometryInstance3D's, so it reaches every surface this node draws, the overlay's too.
    // `cast_shadow`, the range cull and each surface's billboard and shadow-pass membership reach
    // three per draw group, through hooks that read that group's material.
    // The box covers every part, not the shell's alone, as Godot's does.
    const drawnParts = content.kind === 'surfaced' ? content.mesh.parts : NO_PARTS;
    const placement = useMemo(() => partsPlacement(meshRef, drawnParts.map((part) => part.geometry)), [drawnParts]);
    // The placeholder box is not the mesh's, so the cull cannot measure the instance.
    const shadow = useGeometryInstance(content.kind === 'unresolved' ? UNPLACED : placement);
    // The shell is the mesh's first part, which draws triangles when the mesh has any.
    const drawsPoints = content.kind === 'surfaced' && geometryDrawsPoints(content.mesh.parts[0].geometry);
    const skin = useMeshSkin(properties, useNodePath());
    const bindCount = skin?.matrices.length ?? 0;
    // The instance has no bounds only where every surface of every part outgrows the skin.
    const isOutgrown = useMemo(() => drawnParts.length > 0 && drawnParts.every((part) => isOutgrownBySkin(part.geometry, bindCount)), [drawnParts, bindCount]);
    // A surface with no bone arrays stays unskinned even when the node is bound.
    const skeleton = useSkinSkeleton(isOutgrown ? undefined : drawnParts[0]?.geometry, skin);
    const shellProps = {
        name: node.name,
        meshRef,
        drawsPoints,
        skeleton,
        transform,
        shadow,
        godotLayers: properties.layers,
        subtree: gizmo && content.kind === 'surfaced' ? (_jsxs(_Fragment, { children: [gizmo(content.mesh), children] })) : (children),
    };
    switch (content.kind) {
        case 'unresolved':
            return _jsx(MeshShell, { ...shellProps, children: UNRESOLVED_MESH });
        // While it loads, the shell keeps the node's descendants mounted: they do not depend on
        // the `.tres`. A node with no mesh, and a mesh Godot builds no surface for, draw nothing and
        // their descendants still mount. Their `get_aabb()` is `AABB()`, a point to frame on.
        case 'loading':
            return _jsx(MeshShell, { ...shellProps, children: null });
        case 'none':
        case 'empty':
            return (_jsx(MeshShell, { ...shellProps, children: _jsx(FramePoint, {}) }));
        case 'surfaced': {
            // The skin leaves the instance without bounds, so Godot places none of its surfaces.
            if (isOutgrown)
                return _jsx(MeshShell, { ...shellProps, children: null });
            const { parts } = content.mesh;
            return (_jsxs(MeshShell, { ...shellProps, children: [_jsx(MeshSurfaces, { part: parts[0], overrides: meshOverrides, meshRef: meshRef, skeleton: skeleton, godotLayers: properties.layers, overlaySource: overlaySource, shadow: shadow }), parts.slice(1).map((part, i) => (_jsx(FollowingPart, { part: part, overrides: meshOverrides, overlaySource: overlaySource, shadow: shadow, godotLayers: properties.layers }, i)))] }));
        }
    }
}
const NO_PARTS = [];
/**
 * `material_overlay`'s draw: the base mesh's geometry a second time, with the
 * overlay material. A separate mesh, not a material array entry: Godot's
 * overlay covers every surface (`_geometry_instance_add_surface` runs per
 * surface), and one material over a grouped geometry draws the same pixels.
 * A valid overlay's own `next_pass` chain mounts after its draw
 * (`render_forward_clustered.cpp:4230-4239`), its links sharing the base draw's
 * geometry.
 */
function MaterialOverlayMesh({ meshRef, drawsPoints, skeleton, godotLayers, source, shadow, renderOrder, }) {
    // Read back off the base mesh rather than built again, so all four geometry branches
    // share one component and the two meshes share one geometry.
    const geometry = useDrawnGeometry(meshRef);
    if (!geometry)
        return null;
    const { Drawn, skin } = drawnElementOf(drawsPoints, skeleton);
    return (_jsxs(_Fragment, { children: [_jsx(Drawn, { ...skin, geometry: geometry, ...overlayDrawProps(shadow), renderOrder: renderOrder, receiveShadow: true, children: _jsx(SurfaceMaterialSlot, { source: source, points: drawsPoints ? 'mesh' : undefined }) }), _jsx(NextPassLink, { source: source, ordinal: renderOrder, drawsSelf: false, 
                // The overlay drew at this order; its own key lands in the set on recursion.
                visited: NOTHING_DRAWN, geometry: geometry, attach: "material", surfaceCount: 1, drawsPoints: drawsPoints, skeleton: skeleton, godotLayers: godotLayers, shadow: shadow })] }));
}
/**
 * The attribute shell every `<mesh>` branch of MeshInstance3D shares.
 * `children` is the branch's geometry and material. `subtree` is the node's
 * descendants, inside the mesh so they inherit its transform. Every branch,
 * placeholders included, passes it, or the descendants vanish with the mesh.
 */
function MeshShell({ name, meshRef, drawsPoints, skeleton, transform, shadow, godotLayers, subtree, children, }) {
    const { Drawn, skin } = drawnElementOf(drawsPoints, skeleton);
    return (_jsxs(Drawn, { ...skin, ref: meshRef, name: name, ...node3dGroupSpread(transform), ...drawHookProps(shadow), 
        // Godot's `layers`, for the decal and probe masks. Set on every branch, placeholders
        // included, so a decal's receiver test never depends on load order.
        userData: visualLayersUserData(godotLayers), children: [children, subtree] }));
}
/**
 * What an unresolved mesh renders as: a failed external `.tres`, or a scene
 * sub-resource whose surfaces could not be read.
 */
const UNRESOLVED_MESH = (_jsxs(_Fragment, { children: [_jsx("boxGeometry", { args: [1, 1, 1] }), _jsx("meshBasicMaterial", { ...UNRESOLVED_MESH_MATERIAL.props }, UNRESOLVED_MESH_MATERIAL.key)] }));
/**
 * A mesh's geometry plus a `<SurfaceMaterialSlots>` over its draw groups, so one hook per
 * component serves any surface count. Godot sizes the override array to the surface count
 * (`scene/3d/mesh_instance_3d.cpp:68,407`), so the draw groups set the slot count and an extra
 * override is dropped: a PrimitiveMesh takes only surface 0's. Each group's starting material
 * carries its own `next_pass` chain through `<NextPassChain>`, and the part's overlay mounts
 * through the chain, one past the surface chains.
 */
function MeshSurfaces({ part, overrides, meshRef, skeleton, godotLayers, overlaySource, shadow, }) {
    const groupCount = Math.max(part.surfaceIndices.length, 1);
    const sources = Array.from({ length: groupCount }, (_unused, i) => effectiveMaterialSource(overrides, part.surfaceIndices[i] ?? i, part.materials[i]));
    const drawsPoints = geometryDrawsPoints(part.geometry);
    return (_jsxs(_Fragment, { children: [_jsx("primitive", { object: part.geometry, attach: "geometry" }), _jsx(SurfaceMaterialSlots, { sources: sources, points: drawsPoints ? 'mesh' : undefined }), _jsx(NextPassChain, { meshRef: meshRef, sources: sources, drawsPoints: drawsPoints, skeleton: skeleton, godotLayers: godotLayers, shadow: shadow, overlay: overlaySource
                    ? (renderOrder) => (_jsx(MaterialOverlayMesh, { meshRef: meshRef, drawsPoints: drawsPoints, skeleton: skeleton, godotLayers: godotLayers, source: overlaySource, shadow: shadow, renderOrder: renderOrder }))
                    : undefined })] }));
}
/**
 * A part after the shell's own: the points of a mesh that also has triangles. A child of the shell,
 * so it shares the node's transform and its subtree stays under one object. It carries its own
 * overlay, as Godot draws the overlay over every surface.
 */
function FollowingPart({ part, overrides, overlaySource, shadow, godotLayers, }) {
    const ref = useRef(null);
    const drawsPoints = geometryDrawsPoints(part.geometry);
    const Drawn = drawnTag(drawsPoints);
    return (_jsx(Drawn, { ref: ref, ...drawHookProps(shadow), userData: visualLayersUserData(godotLayers), children: _jsx(MeshSurfaces, { part: part, overrides: overrides, meshRef: ref, godotLayers: godotLayers, overlaySource: overlaySource, shadow: shadow }) }));
}
function resolveMeshOverrides(properties, scene) {
    const perSurface = new Map();
    for (const [index, ref] of properties.surfaceMaterialOverrides ?? []) {
        const source = resolveMaterialSource(ref, scene);
        if (source)
            perSurface.set(index, source);
    }
    return {
        node: resolveMaterialSource(properties.materialOverride, scene),
        perSurface,
    };
}
/**
 * What Godot binds for one surface: the instance's surface override over the
 * mesh's material (`render_forward_clustered.cpp:4264`), then `material_override`
 * in front (`:4206`), else the default material (`:4221`), as
 * `MeshInstance3D::get_active_material` (`scene/3d/mesh_instance_3d.cpp:384`) restates.
 */
function effectiveMaterialSource(overrides, surfaceIndex, own) {
    return overrides.node ?? overrides.perSurface.get(surfaceIndex) ?? own;
}
//# sourceMappingURL=Component.js.map